import { cn } from '../../utils/helpers';
import { forwardRef } from 'react';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, className, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-xs text-text-muted mb-1 font-medium">
            {label}
          </label>
        )}
        <input
          ref={ref}
          className={cn(
            'w-full bg-bg-input border border-border rounded-sm px-3 py-2 text-sm text-text-primary',
            'placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20',
            'transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
            error && 'border-status-critical focus:border-status-critical',
            className
          )}
          {...props}
        />
        {error && <p className="mt-1 text-xs text-status-critical">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: { value: string; label: string }[];
  error?: string;
}

export function Select({ label, options, error, className, ...props }: SelectProps) {
  return (
    <div className="w-full">
      {label && <label className="block text-xs text-text-muted mb-1 font-medium">{label}</label>}
      <select
        className={cn(
          'w-full bg-bg-input border border-border rounded-sm px-3 py-2 text-sm text-text-primary',
          'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20',
          'transition-colors disabled:opacity-50',
          error && 'border-status-critical',
          className
        )}
        {...props}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-status-critical">{error}</p>}
    </div>
  );
}

export { Input };
